import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getStoredAuthUser } from "@/lib/authUser";
import { formatCurrency, getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";
import { pkrAmountInWords } from "@/lib/formatPkrAmount";
import type { SaleInvoiceRecord, SalePaymentRecord } from "@/lib/sales/saleInvoiceStore";
import { format, isValid, parseISO } from "date-fns";
import { Car, CheckCircle2, FileText, Printer, Receipt, ShieldCheck } from "lucide-react";
import { useRef } from "react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sale: SaleInvoiceRecord;
  payment: SalePaymentRecord | null;
  receiptId: string;
};

/** Convert number to title-cased words + 'Only' suffix (e.g. Sixty Two Thousand Rupees Only) */
function formatFullAmountWords(amount: number): string {
  const words = pkrAmountInWords(Math.round(amount));
  if (!words) return "";
  const titleCased = words
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
  return `${titleCased} Only`;
}

export function PaymentReceiptDialog({
  open,
  onOpenChange,
  sale,
  payment,
  receiptId,
}: Props) {
  const curr = getActiveCompanyCurrency();
  const authUser = getStoredAuthUser();

  const receivedByName = authUser?.name || "Authorized Cashier";
  const receivedByDesignation =
    authUser?.role?.replace(/_/g, " ") ||
    (authUser?.roles?.[0] ? authUser.roles[0].replace(/_/g, " ") : "") ||
    "Accounts / Cashier";

  if (!payment) return null;

  const paymentDate = payment.payment_date ? parseISO(payment.payment_date) : null;
  const createdAt = payment.created_at ? parseISO(payment.created_at) : null;
  const isToken =
    (payment.transaction_type ?? "").toLowerCase().includes("token") ||
    (payment.transaction_type_label ?? "").toLowerCase().includes("token");

  const totalSale = Number(sale.total_amount ?? 0);
  const paidTotal = Number(sale.paid_total ?? 0);
  const outstanding = Number(sale.outstanding ?? sale.balance_outstanding ?? 0);
  const paymentAmount = Number(payment.amount ?? 0);
  const amountWords = formatFullAmountWords(paymentAmount);

  const companyLogo = authUser?.company_branding?.logo?.trim() || null;
  const companyName = authUser?.company_branding?.name || "SHOWROOM INVENTORY";
  const companyAddress = authUser?.company_branding?.address_line || "";
  const companyPhone = authUser?.company_branding?.phone || "";

  const customerName = sale.customer?.user?.name || sale.customer?.contact_person || "Customer";
  const vehicleName = sale.product?.name || "Vehicle";
  const saleType = sale.is_installment
    ? "Installment Deal"
    : isToken
    ? "Booking Token Advance"
    : "Outright Sale";

  const paymentMethodLabel = (payment.payment_method || "cash").replace(/_/g, " ").toUpperCase();
  const paymentChannel = payment.bank_name ? `Bank: ${payment.bank_name}` : "Cash Counter";
  const transactionTypeLabel = isToken ? "Token / Advance Deposit" : (payment.transaction_type_label || "Balance Payment");
  const referenceMemo = [
    payment.transaction_reference_no ? `Ref: ${payment.transaction_reference_no}` : null,
    payment.pay_order_no ? `PO #: ${payment.pay_order_no}` : null,
    payment.notes ? `"${payment.notes}"` : null,
  ].filter(Boolean).join(" • ") || "—";

  const formattedTxDate = paymentDate && isValid(paymentDate)
    ? format(paymentDate, "MMMM d, yyyy")
    : payment.payment_date || "—";
  const formattedTxTime = createdAt && isValid(createdAt) ? `${format(createdAt, "hh:mm a")} PKT` : "";

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      window.print();
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payment Receipt - ${receiptId}</title>
          <style>
            @page { size: A4 portrait; margin: 12mm 15mm; }
            * { box-sizing: border-box; }
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
              color: #0f172a;
              margin: 0;
              padding: 0;
              font-size: 12px;
              line-height: 1.4;
              background: #ffffff;
            }
            .receipt-wrapper {
              border: 1.5px solid #0f172a;
              border-radius: 8px;
              padding: 24px 28px;
              max-width: 760px;
              margin: 0 auto;
            }
            .company-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 2px solid #0f172a;
              padding-bottom: 12px;
              margin-bottom: 16px;
            }
            .company-logo {
              max-height: 52px;
              max-width: 220px;
              object-fit: contain;
              margin-bottom: 4px;
            }
            .company-name {
              font-size: 20px;
              font-weight: 800;
              color: #0f172a;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin: 0;
            }
            .company-meta {
              font-size: 11px;
              color: #475569;
              margin-top: 3px;
            }
            .receipt-badge-col {
              text-align: right;
            }
            .receipt-title {
              font-size: 12px;
              font-weight: 800;
              color: #0f172a;
              text-transform: uppercase;
              letter-spacing: 1px;
              margin: 0 0 3px 0;
            }
            .receipt-number {
              font-family: monospace;
              font-size: 14px;
              font-weight: 800;
              color: #1e40af;
              background: #eff6ff;
              border: 1px solid #bfdbfe;
              padding: 3px 8px;
              border-radius: 4px;
              display: inline-block;
            }
            .meta-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 14px;
            }
            .meta-table td {
              padding: 8px 10px;
              border: 1px solid #cbd5e1;
              font-size: 11.5px;
            }
            .meta-table .label {
              background-color: #f8fafc;
              font-weight: 700;
              color: #475569;
              width: 22%;
              text-transform: uppercase;
              font-size: 10px;
              letter-spacing: 0.5px;
            }
            .meta-table .val {
              width: 28%;
              color: #0f172a;
              font-weight: 600;
            }
            .amount-banner {
              background: #f0fdf4;
              border: 1.5px solid #86efac;
              border-radius: 6px;
              padding: 12px;
              text-align: center;
              margin: 14px 0;
            }
            .amount-label {
              font-size: 10px;
              font-weight: 800;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #166534;
              margin-bottom: 2px;
            }
            .amount-figure {
              font-size: 24px;
              font-weight: 900;
              color: #15803d;
              margin: 2px 0;
              font-family: monospace;
            }
            .amount-words {
              font-size: 11.5px;
              font-weight: 700;
              color: #14532d;
              background: #dcfce7;
              display: inline-block;
              padding: 3px 12px;
              border-radius: 4px;
              margin-top: 3px;
            }
            .financials-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 14px;
              background: #f8fafc;
              border: 1px solid #cbd5e1;
              border-radius: 6px;
            }
            .financials-table td {
              padding: 7px 12px;
              border-bottom: 1px solid #e2e8f0;
              font-size: 11.5px;
            }
            .financials-table tr:last-child td {
              border-bottom: none;
              font-weight: 800;
              font-size: 12.5px;
              background: #f1f5f9;
            }
            .signatures-grid {
              display: flex;
              justify-content: space-between;
              margin-top: 110px;
              padding-top: 12px;
            }
            .signature-card {
              width: 44%;
              text-align: center;
              border-top: 1.5px dashed #475569;
              padding-top: 8px;
            }
            .signature-card .name {
              font-size: 11.5px;
              font-weight: 700;
              color: #0f172a;
              margin: 0;
            }
            .signature-card .role {
              font-size: 10.5px;
              color: #64748b;
              margin: 1px 0 0 0;
              text-transform: capitalize;
            }
            .signature-card .tag {
              font-size: 9.5px;
              color: #94a3b8;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-top: 3px;
            }
            .footer-note {
              margin-top: 16px;
              text-align: center;
              font-size: 9.5px;
              color: #94a3b8;
              border-top: 1px solid #f1f5f9;
              padding-top: 6px;
            }
          </style>
        </head>
        <body>
          <div class="receipt-wrapper">
            <!-- Header -->
            <div class="company-header">
              <div>
                ${
                  companyLogo
                    ? `<img src="${companyLogo}" alt="${companyName}" class="company-logo" />`
                    : `<h1 class="company-name">${companyName}</h1>`
                }
                <div class="company-meta">
                  ${companyAddress ? `<span>${companyAddress}</span><br />` : ""}
                  ${companyPhone ? `<span>Ph: ${companyPhone}</span> • ` : ""}
                  <span>Official Sale Invoice #${sale.invoice_number || `INV-${sale.id}`}</span>
                </div>
              </div>
              <div class="receipt-badge-col">
                <div class="receipt-title">PAYMENT RECEIPT VOUCHER</div>
                <div class="receipt-number">${receiptId}</div>
                <div style="font-size: 10px; color: #64748b; margin-top: 4px;">
                  ${formattedTxDate} ${formattedTxTime ? `• ${formattedTxTime}` : ""}
                </div>
              </div>
            </div>

            <!-- Amount Banner with Full Words -->
            <div class="amount-banner">
              <div class="amount-label">AMOUNT RECEIVED</div>
              <div class="amount-figure">${formatCurrency(paymentAmount)}</div>
              ${amountWords ? `<div class="amount-words">${amountWords}</div>` : ""}
            </div>

            <!-- Vehicle & Payment Details Table -->
            <table class="meta-table">
              <tr>
                <td class="label">Vehicle Sold</td>
                <td class="val">${vehicleName}</td>
                <td class="label">Deal / Status</td>
                <td class="val">${saleType}</td>
              </tr>
              <tr>
                <td class="label">Payment Method</td>
                <td class="val">${paymentMethodLabel}</td>
                <td class="label">Channel / Bank</td>
                <td class="val">${paymentChannel}</td>
              </tr>
              <tr>
                <td class="label">Transaction Type</td>
                <td class="val">${transactionTypeLabel}</td>
                <td class="label">Reference / Memo</td>
                <td class="val">${referenceMemo}</td>
              </tr>
            </table>

            <!-- Financial Breakdown Table -->
            <table class="financials-table">
              <tr>
                <td style="color: #475569;">Agreed Vehicle Sale Price:</td>
                <td style="text-align: right; font-weight: 700; font-family: monospace;">${formatCurrency(totalSale)}</td>
              </tr>
              <tr>
                <td style="color: #475569;">Total Realized Payments to Date:</td>
                <td style="text-align: right; font-weight: 700; color: #16a34a; font-family: monospace;">${formatCurrency(paidTotal)}</td>
              </tr>
              <tr>
                <td style="color: #0f172a;">Remaining Invoice Balance Due:</td>
                <td style="text-align: right; font-family: monospace; ${outstanding <= 0.01 ? "color: #16a34a;" : "color: #d97706;"}">
                  ${formatCurrency(outstanding)}
                </td>
              </tr>
            </table>

            <!-- Signatures Section -->
            <div class="signatures-grid">
              <div class="signature-card">
                <p class="name">${customerName}</p>
                <p class="tag">Customer / Depositor Signature</p>
              </div>
              <div class="signature-card">
                <p class="name">${receivedByName}</p>
                <p class="role">${receivedByDesignation}</p>
                <p class="tag">Authorized Showroom Signature &amp; Stamp</p>
              </div>
            </div>

            <!-- Footer Note -->
            <div class="footer-note">
              This is a computer-generated payment receipt voucher issued by ${companyName}.
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
              <Receipt className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-foreground">
                Payment Receipt Voucher
              </DialogTitle>
              <p className="text-xs text-muted-foreground">
                Receipt #{receiptId} • Linked to Invoice #{sale.invoice_number || `INV-${sale.id}`}
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Modal Visual Area */}
        <div className="space-y-4 py-2 text-foreground">
          {/* Top Receipt Identity */}
          <div className="flex items-center justify-between border-b border-border/40 pb-3">
            <div>
              {companyLogo ? (
                <img src={companyLogo} alt={companyName} className="h-10 max-w-[200px] object-contain mb-1.5" />
              ) : (
                <h4 className="text-base font-bold text-foreground">{companyName}</h4>
              )}
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                OFFICIAL PAYMENT RECEIPT
              </span>
              <h3 className="text-sm font-extrabold text-foreground font-mono">
                {receiptId}
              </h3>
            </div>
            <div className="text-right">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                TRANSACTION DATE
              </span>
              <p className="text-xs font-bold text-foreground">
                {formattedTxDate}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {formattedTxTime}
              </p>
            </div>
          </div>

          {/* Amount Box */}
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-center">
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 dark:text-emerald-300">
              AMOUNT RECEIVED
            </span>
            <p className="text-2xl font-black text-emerald-700 dark:text-emerald-400 tabular-nums my-0.5 font-mono">
              {formatCurrency(paymentAmount)}
            </p>
            {amountWords ? (
              <span className="inline-block text-xs font-bold px-3 py-0.5 rounded bg-emerald-500/20 text-emerald-900 dark:text-emerald-200">
                {amountWords}
              </span>
            ) : null}
          </div>

          {/* Vehicle & Payment Details Table */}
          <div className="rounded-lg border border-border/50 bg-muted/15 overflow-hidden text-xs">
            <div className="grid grid-cols-2 divide-x divide-border/40 border-b border-border/40 p-2.5">
              <div>
                <span className="text-[10px] font-bold uppercase text-muted-foreground">VEHICLE DETAILS</span>
                <p className="font-bold text-foreground mt-0.5">{vehicleName}</p>
              </div>
              <div className="pl-3">
                <span className="text-[10px] font-bold uppercase text-muted-foreground">DEAL / STATUS</span>
                <p className="font-bold text-foreground mt-0.5">{saleType}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 divide-x divide-border/40 border-b border-border/40 p-2.5">
              <div>
                <span className="text-[10px] font-bold uppercase text-muted-foreground">PAYMENT METHOD</span>
                <p className="font-bold text-foreground mt-0.5">{paymentMethodLabel}</p>
              </div>
              <div className="pl-3">
                <span className="text-[10px] font-bold uppercase text-muted-foreground">CHANNEL / BANK</span>
                <p className="font-bold text-foreground mt-0.5">{paymentChannel}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 divide-x divide-border/40 p-2.5">
              <div>
                <span className="text-[10px] font-bold uppercase text-muted-foreground">TRANSACTION TYPE</span>
                <p className="font-bold text-foreground mt-0.5">{transactionTypeLabel}</p>
              </div>
              <div className="pl-3">
                <span className="text-[10px] font-bold uppercase text-muted-foreground">REFERENCE / MEMO</span>
                <p className="text-foreground mt-0.5 font-medium">{referenceMemo}</p>
              </div>
            </div>
          </div>

          {/* Invoice Financial Snapshot */}
          <div className="rounded-lg border border-border/40 bg-background/80 p-3 space-y-1.5 text-xs">
            <div className="flex justify-between items-center text-muted-foreground">
              <span>Agreed Vehicle Sale Price:</span>
              <span className="font-bold text-foreground tabular-nums font-mono">{formatCurrency(totalSale)}</span>
            </div>
            <div className="flex justify-between items-center text-muted-foreground">
              <span>Total Collections to Date:</span>
              <span className="font-bold text-emerald-600 dark:text-emerald-400 tabular-nums font-mono">
                {formatCurrency(paidTotal)}
              </span>
            </div>
            <div className="flex justify-between items-center border-t border-border/40 pt-1.5 font-bold text-sm">
              <span className="text-foreground">Remaining Invoice Balance:</span>
              <span
                className={
                  outstanding <= 0.01
                    ? "text-emerald-600 dark:text-emerald-400 tabular-nums font-mono"
                    : "text-amber-600 dark:text-amber-400 tabular-nums font-mono"
                }
              >
                {formatCurrency(outstanding)}
              </span>
            </div>
          </div>

          {/* Signature Lines */}
          <div className="grid grid-cols-2 gap-8 pt-6 text-center text-xs">
            <div className="border-t border-dashed border-border/70 pt-2">
              <p className="font-bold text-foreground">{customerName}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                Customer / Depositor Signature
              </p>
            </div>
            <div className="border-t border-dashed border-border/70 pt-2">
              <p className="font-bold text-foreground">{receivedByName}</p>
              <p className="text-[11px] font-medium text-muted-foreground capitalize">{receivedByDesignation}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/80 mt-0.5">
                Authorized Showroom Signature &amp; Stamp
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-border/40 pt-3 flex items-center justify-between sm:justify-between">
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-1.5 bg-primary font-semibold shadow-xs"
            onClick={handlePrint}
          >
            <Printer className="w-4 h-4" />
            Print Receipt Voucher
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
